/**
 * Authenticated admin API for the partner review queue. `PartnerViewSet` is
 * Form-centric (see partners/views.py's class docstring) — every action but
 * `list` addresses the tenant's singleton `partner` Form by its own id, so
 * callers fetch that Form first and thread its id into `getQueue`/`review`.
 */
import apiClient from './client';

export interface PartnerForm {
  id: number;
}

export interface PartnerQueueRow {
  id: number;
  school_name: string | null;
  status: string;
  created_at: string;
}

// Mirrors partners.form_types.STATUS_CHOICES — see that module's docstring
// for the state machine diagram this walks.
export const PARTNER_STATUSES: { value: string; label: string }[] = [
  { value: 'imported', label: 'Imported' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'published', label: 'Published' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'partnered', label: 'Partnered' },
];

export interface PartnerRecord {
  id: number;
  school_name: string | null;
  status: string;
  state: string | null;
  city: string | null;
  claimed: boolean;
  rejection_reason: string;
  created_at: string;
  updated_at: string;
}

export interface PartnerProfileField {
  name: string;
  label: string;
  value: string | null;
}

export type PartnerReviewStatus = 'published' | 'rejected' | 'submitted';

// Shared between the roster table and the detail page's status sidebar.
export function partnerStatusLabel(status: string): string {
  return PARTNER_STATUSES.find((s) => s.value === status)?.label ?? status;
}

export function partnerStatusChipClass(status: string): string {
  switch (status) {
    case 'published':
    case 'partnered':
      return 'sgk-chip sgk-chip--live';
    case 'submitted':
      return 'sgk-chip sgk-chip--sprint';
    case 'rejected':
      return 'sgk-chip sgk-chip--error';
    default:
      return 'sgk-chip sgk-chip--off';
  }
}

export interface PartnerMyListing {
  id: number;
  status: string;
  school_name: string | null;
  rejection_reason: string;
  /** The state machine still lets the school change this record. */
  editable: boolean;
  /** Only a staff-published listing has a public page. */
  public_path: string | null;
  fields: PartnerProfileField[];
}

export interface PartnerClaimable {
  id: number;
  school_name: string | null;
  city: string | null;
  state: string | null;
}

export interface PartnerMine {
  listing: PartnerMyListing | null;
  claimable: PartnerClaimable | null;
}

export interface PartnerFilterableField {
  name: string;
  label: string;
}

export interface ImportRowError {
  row: number;
  field: string;
  message: string;
}

export interface BulkImportResult {
  imported: number;
  skipped: number;
  errors: ImportRowError[];
  error?: string;
  failed_at_row?: number;
}

export interface AddPartnerFieldErrors {
  error: string;
  field_errors?: Record<string, string[]>;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const partnersApi = {
  // One `partner`-type Form per tenant (seeded on organization creation —
  // see partners/signals.py), so the first result is the only one.
  getForm: (): Promise<PartnerForm | null> =>
    apiClient
      .get('/partners/')
      .then((r) => (r.data as Paginated<PartnerForm>).results[0] ?? null),

  // AllowAny — for the self-service "join the directory" page
  // (`/partners/join`), which has no admin session and so can't reach
  // `getForm` above (`list` is a `'design'`-role action).
  getPublicForm: (): Promise<PartnerForm> =>
    apiClient.get('/partners/public_form/').then((r) => r.data),

  // AllowAny — the account-first join's front door: creates (or reuses) the
  // school's partner login for `email` and mails a link that lands back on
  // /partners/join. Always resolves `sent` for a well-formed email, whether or
  // not anything was sent (see PartnerViewSet.start_join).
  startJoin: (formId: number, email: string, next?: string): Promise<{ status: 'sent' }> =>
    apiClient.post(`/partners/${formId}/start_join/`, { email, next }).then((r) => r.data),

  // The signed-in school's dashboard payload (partner accounts only; 403 for
  // anyone else) — see PartnerViewSet.mine.
  mine: (formId: number): Promise<PartnerMine> =>
    apiClient.get(`/partners/${formId}/mine/`).then((r) => r.data),

  // The school edits its own listing. Not formkit's stock resubmit, which
  // would force a live listing back to `submitted` — see
  // PartnerViewSet.update_mine. The server picks the row (the caller's own);
  // nothing in `data` can name another.
  updateMine: (formId: number, data: Record<string, unknown>): Promise<{ id: number; status: string }> =>
    apiClient.post(`/partners/${formId}/update_mine/`, data).then((r) => r.data),

  // Accept the imported listing `mine` offered as `claimable`; the server
  // re-checks that its email matches the signed-in account's.
  confirmClaim: (formId: number, submissionId: number): Promise<{ status: 'claimed' }> =>
    apiClient
      .post(`/partners/${formId}/confirm_claim/`, { submission_id: submissionId })
      .then((r) => r.data),

  getQueue: (formId: number): Promise<PartnerQueueRow[]> =>
    apiClient.get(`/partners/${formId}/queue/`).then((r) => r.data),

  // Every status, unlike getQueue's submitted-only scope — the admin
  // table's data source, and (found by id) the detail page's status sidebar.
  getRecords: (formId: number): Promise<PartnerRecord[]> =>
    apiClient.get(`/partners/${formId}/records/`).then((r) => r.data),

  // The detail page's read-only "Profile" panel — field label/value pairs,
  // choice fields already resolved to their label server-side. See
  // partners/views.py's PartnerViewSet.profile / _resolve_choice_label for
  // why this doesn't reuse FormRenderer to display an existing submission.
  getProfile: (formId: number, submissionId: number): Promise<PartnerProfileField[]> =>
    apiClient
      .get(`/partners/${formId}/profile/`, { params: { submission_id: submissionId } })
      .then((r) => r.data),

  // The admin-facing picker behind PartnerMap's `filterField`
  // config — see partners.views.PartnerViewSet.filterable_fields.
  filterableFields: (formId: number): Promise<PartnerFilterableField[]> =>
    apiClient.get(`/partners/${formId}/filterable_fields/`).then((r) => r.data),

  review: (
    formId: number,
    submissionId: number,
    status: PartnerReviewStatus,
    rejectionReason?: string,
  ): Promise<{ id: number; status: string; rejection_reason: string }> =>
    apiClient
      .post(`/partners/${formId}/review/`, {
        submission_id: submissionId,
        status,
        rejection_reason: rejectionReason,
      })
      .then((r) => r.data),

  // Staff-entered single listing (the header's "Add partner" button) — lands
  // as `imported`, same as a bulk-imported row. `data` is the flat field map
  // FormRenderer produces.
  addPartner: (formId: number, data: Record<string, unknown>): Promise<{ id: number; status: string }> =>
    apiClient.post(`/partners/${formId}/add_partner/`, data).then((r) => r.data),

  // A blob, not a URL: the download needs the same token auth header every
  // other request carries, which a plain <a href> can't attach.
  downloadImportTemplate: (formId: number): Promise<Blob> =>
    apiClient
      .get(`/partners/${formId}/import_template/`, { responseType: 'blob' })
      .then((r) => r.data),

  bulkImport: (formId: number, file: File): Promise<BulkImportResult> => {
    const body = new FormData();
    body.append('file', file);
    return apiClient
      .post(`/partners/${formId}/bulk_import/`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};
